import type {
  SchemaCondition,
  SchemaForm,
  SchemaField,
  SchemaFieldNumber,
  SchemaFieldSelect,
  SchemaFieldSeveritySection,
  SchemaFieldText,
  SchemaGroup,
  SchemaOperator,
  SchemaOperatorEmpty,
} from "./schema.type";

import { isValidObject } from "@/utilities/object";

import { SCHEMA_EMPTY_OPERATORS, SCHEMA_OPERATORS } from "./schema.constant";

/**
 * SchemaForm
 */

export const schemaFormHasContent = (schema?: SchemaForm): boolean => {
  return schemaFormHasFields(schema) || schemaFormHasGroups(schema);
};

export const schemaFormHasFields = (schema?: SchemaForm): boolean => {
  const fields = schema?.fields ?? [];
  if (!fields.length) return false;
  if (!fields.every(schemaFieldHasContent)) return false;
  return true;
};

export const schemaFormHasGroups = (schema?: SchemaForm): boolean => {
  const groups = schema?.groups ?? [];
  if (!groups.length) return false;
  if (!groups.every(schemaGroupHasContent)) return false;
  return true;
};

/**
 * SchemaField
 */

export const schemaFieldHasContent = (field?: SchemaField): boolean => {
  if (!field) return false;
  if (typeof field.model !== "string") return false;
  if (typeof field.type !== "string") return false;
  return true;
};

/**
 * SchemaGroup
 */

export const schemaGroupHasContent = (
  group?: SchemaGroup
): group is SchemaGroup => {
  if (!group?.fields?.length) return false;
  if (!group.fields.every(schemaFieldHasContent)) return false;
  return true;
};

/**
 * Specifics SchemaField
 */

export const isSchemaFieldText = (
  schema: SchemaField
): schema is SchemaFieldText => {
  return schema.type === "input" && schema.inputType === "text";
};

export const isSchemaFieldNumber = (
  schema: SchemaField
): schema is SchemaFieldNumber => {
  return schema.type === "input" && schema.inputType === "number";
};

export const isSchemaFieldSelect = (
  schema: SchemaField
): schema is SchemaFieldSelect => {
  return schema.type === "select" && !isSchemaFieldSeveritySection(schema);
};

export const isSchemaFieldSeveritySection = (
  schema: SchemaField
): schema is SchemaFieldSeveritySection => {
  return "isFrozen" in schema && schema.isFrozen === true;
};

export const isSchemaOperator = (
  operator: string
): operator is SchemaOperator =>
  SCHEMA_OPERATORS.includes(operator as SchemaOperator);

export const isSchemaOperatorEmpty = (
  operator: string
): operator is SchemaOperatorEmpty =>
  SCHEMA_EMPTY_OPERATORS.includes(operator as SchemaOperatorEmpty);

export const isSchemaConditionValue = (
  value?: unknown
): value is SchemaCondition => {
  if (!value) return false;
  if (!isValidObject(value)) return false;
  if (!("model" in value && value.model)) return false;
  if (!("condition" in value && value.condition)) return false;
  if (!(typeof value.condition === "string")) return false;
  return isSchemaOperator(value.condition);
};
